import { useAppContext } from "AppContext";
import { useTranslation } from "react-i18next";
import { useWidgetDetails } from "./useWidgetDetails";
import {
  EmbedWidgetModal,
  getWidgetDetailsTabsConfig,
  WidgetPreviewModal,
} from "./components";
import { useEffect, useRef, useState } from "react";
import { WidgetDetailsTabEnum } from "./WidgetDetailsTabEnum";
import { useWidgetDetailsForm } from "./views/WidgetDetails/useWidgetDetailsForm";
import {
  EnumListType,
  EnumWidgetType,
  ListByIdQuery,
  WidgetByIdQuery,
} from "gql/generated";
import { useToastContext } from "@ttc3k/trekker";
import { getMultilingualString } from "shared/utils";
import {
  AppLayout,
  LoadingPage,
  NavigationBlocker,
  PageSidebar,
  SimpleErrorNotification,
} from "shared/components";
import {
  WidgetDetailsView,
  WidgetItems,
  WidgetNotes,
  WidgetSetup,
} from "./views";
import { PageActions } from "shared/components/organisms/PageActions";
import { Divider, Flex } from "styled-system/jsx";
import { WidgetDetailsContextProvider } from "./WidgetDetailsContextProvider";
import { BuildNewListModal } from "apps/customLists/components";
import { ListCriteria, SourceList, WidgetDetailsTabs } from "./components";
import { useParams } from "react-router-dom";
import { WidgetDetailsPageActionKey } from "./_widgetDetails.config";
import { WidgetItemsEvents } from "./views/WidgetItemsEvents";
import { WidgetSetupEvents } from "./views/WidgetSetupEvents";

export const WidgetDetails = () => {
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();
  const { organizationId } = useParams();
  const { setIsNavigationBlocked, organizations } = useAppContext();
  const {
    widget,
    error,
    loading,
    isListEditingModalOpen,
    isEmbedModalOpen,
    pageActions,
    pageActionToDisable,
    widgetFilters,
    handleActionClick,
    handleEditListItemsClick,
    setIsEmbedModalOpen,
    refetchWidget,
    isPreviewWidgetModalOpen,
    setIsPreviewWidgetModalOpen,
  } = useWidgetDetails();

  /**
   * Setup the tabs
   */
  const widgetDetailsTabsConfig = getWidgetDetailsTabsConfig(t);
  const [tab, setTab] = useState<WidgetDetailsTabEnum>(
    widgetDetailsTabsConfig[0].value as WidgetDetailsTabEnum,
  );

  /**
   * Setup the widget details form
   */
  const formIdWidgetDetails = "widget-details-form";
  const { form: formWidgetDetails, onSubmit: onSubmitWidgetDetails } =
    useWidgetDetailsForm({
      widget: widget as WidgetByIdQuery["widgetById"],
      onSuccess: () => {
        formWidgetDetails.reset();
        toastFactory.create({
          title: t("core:SUCCESS"),
          description: t("widgets:DETAILS.FORM.SUCCESS", {
            widgetName: getMultilingualString(widget?.name ?? {}),
          }),
        });
      },
      onError: () => {
        toastFactory.create({
          title: t("core:ERROR.TITLE"),
          description: t("widgets:DETAILS.FORM.ERROR", {
            widgetName: getMultilingualString(widget?.name ?? {}),
          }),
        });
      },
    });

  useEffect(() => {
    setIsNavigationBlocked(formWidgetDetails.formState.isDirty);
  }, [formWidgetDetails.formState.isDirty, setIsNavigationBlocked]);

  // We need a ref of the scrollable element to pass to the virtualizer
  const virtualScrollRef = useRef<HTMLDivElement>(null);

  if (loading) return <LoadingPage />;

  if (error) throw new Error(error.message);

  if (!widget) return <SimpleErrorNotification />;

  /**
   * Handle tab changes and displaying the correct tab content
   */
  const handleTabChange = (tab: WidgetDetailsTabEnum) => {
    setTab(tab);
  };

  const getCurrentListDetailsView = (tab: WidgetDetailsTabEnum) => {
    switch (tab) {
      case WidgetDetailsTabEnum.WidgetItems:
        return widget.type === EnumWidgetType.Operator ? (
          <WidgetItems virtualScrollRef={virtualScrollRef} />
        ) : (
          <WidgetItemsEvents virtualScrollRef={virtualScrollRef} />
        );
      case WidgetDetailsTabEnum.Setup:
        return widget.type === EnumWidgetType.Operator ? (
          <WidgetSetup
            onPreviewWidgetClick={() =>
              handleActionClick(WidgetDetailsPageActionKey.PreviewWidget)
            }
          />
        ) : (
          <WidgetSetupEvents
            form={formWidgetDetails}
            formId={formIdWidgetDetails}
            onSubmit={onSubmitWidgetDetails}
            onOpenPreviewModal={() =>
              handleActionClick(WidgetDetailsPageActionKey.PreviewWidget)
            }
          />
        );
      case WidgetDetailsTabEnum.Details:
        return (
          <WidgetDetailsView
            onOpenPreviewModal={() => setIsPreviewWidgetModalOpen(true)}
            form={formWidgetDetails}
            formId={formIdWidgetDetails}
            onSubmit={onSubmitWidgetDetails}
          />
        );
      case WidgetDetailsTabEnum.Notes:
        return <WidgetNotes />;
    }
  };

  /**
   * Get the current organization details
   */
  const organization = organizations.find((org) => org._id === organizationId);

  return (
    <WidgetDetailsContextProvider widget={widget}>
      <AppLayout
        ref={virtualScrollRef}
        sidebar={
          <PageSidebar
            title={getMultilingualString(widget.name)}
            buttonGroupProps={{
              py: "150",
            }}
            /* EMBED WIDGET BUTTON */
            primaryButtonProps={{
              visual: "accent.blue",
              children: t("widgets:DETAILS.CTA_BUTTONS.EMBED_COPY"),
              onClick: () => setIsEmbedModalOpen(true),
            }}
            /* SAVE CHANGES BUTTON */
            secondaryButtonProps={
              (tab === WidgetDetailsTabEnum.Details ||
                (widget.type === EnumWidgetType.Event &&
                  tab === WidgetDetailsTabEnum.Setup)) && {
                visual: "primary",
                children: t("widgets:DETAILS.CTA_BUTTONS.SAVE_COPY"),
                isLoading: formWidgetDetails.formState.isSubmitting,
                disabled: !formWidgetDetails.formState.isDirty,
                form: formIdWidgetDetails,
                type: "submit",
              }
            }
          >
            <Flex
              flexDir="column"
              justifyContent={"flex-start"}
              alignItems={"flex-start"}
              gap="300"
            >
              <PageActions
                title={t("core:ACTIONS")}
                actions={pageActions}
                onActionClick={handleActionClick}
                keyToDisable={pageActionToDisable}
              />

              {widget.type === EnumWidgetType.Operator && widget.list && (
                <>
                  <Divider color="border.light" height="1" />
                  <SourceList
                    widget={widget}
                    onEditListItemsClick={handleEditListItemsClick}
                  />

                  {widget.list.type === EnumListType.Smart && (
                    <>
                      <Divider color="border.light" height="1" />
                      <ListCriteria widgetFilters={widgetFilters} />
                    </>
                  )}
                </>
              )}
            </Flex>
          </PageSidebar>
        }
        content={
          <Flex direction={"column"} h={"fit-content"} w={"full"} gap={"300"}>
            <NavigationBlocker>
              <WidgetDetailsTabs
                currentTab={tab}
                onTabChange={handleTabChange}
              />
            </NavigationBlocker>

            {getCurrentListDetailsView(tab)}
          </Flex>
        }
      />
      {widget.type === EnumWidgetType.Operator && widget.list && (
        <BuildNewListModal
          isOpen={isListEditingModalOpen}
          onClose={handleEditListItemsClick}
          name={getMultilingualString(widget.list.name)}
          widgetName={getMultilingualString(widget.name)}
          type={widget.list.type}
          defaultList={widget.list as ListByIdQuery["listById"]}
          onSaveComplete={refetchWidget}
        />
      )}
      <WidgetPreviewModal
        isOpen={isPreviewWidgetModalOpen}
        onClose={() => setIsPreviewWidgetModalOpen(false)}
        widgetId={widget._id}
        widgetType={widget.type}
        organizationWebsite={organization?.contact?.links?.website ?? undefined}
        organizationId={organizationId ?? ""}
      />
      <EmbedWidgetModal
        isOpen={isEmbedModalOpen}
        onClose={() => setIsEmbedModalOpen(false)}
        widgetId={widget._id}
        widgetType={widget.type}
        accessId={organizationId ?? ""}
      />
    </WidgetDetailsContextProvider>
  );
};
